import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { withConfig } from 'src/withConfig';

interface RaceSliderWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

export const RaceSliderWrapper = withConfig(theme => styled('div', { label: 'RaceSliderWrapper' })<RaceSliderWrapperPropsType>`
    background-color: ${({variant}) => variant === DefaultComponentVariantType.TERTIARY ? theme.star.unified.filtersColors.tertiary.bgWrapperColor : theme.star.racingNextOff.slider.bgColorSecondary};
    padding: ${({variant}) => variant === DefaultComponentVariantType.TERTIARY ? '0 12px 12px' : '8px 0 8px 8px'};
    position: relative;
    width: 100%;
`);
interface RaceSwiperWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

export const RaceSwiperWrapper = styled('div', { label: 'RaceSwiperWrapper' })<RaceSwiperWrapperPropsType>`
    overflow: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? 'hidden': 'visible'};
`;

export interface RacingCardsSwiperPropsType {
    inProgress: boolean;
    calcShift?: number;
}

export const RacingCardsSwiper = styled('nav', { label: 'RacingCardsSwiper' })<RacingCardsSwiperPropsType>`
    align-items: flex-start;
    display: flex;
    flex-flow: row nowrap;
    gap: 8px;
    scrollbar-width: none;
    transform: ${({ calcShift }): string => calcShift === undefined ? '' : `translateX(${calcShift}px)`};
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    &::-webkit-scrollbar {
        display: none;
    }
`;

export const HomePageHeader = withConfig(theme => styled('div', { label: 'HomePageHeader' })<SportPageHeaderPropsType>`
    align-items: center;
    background-color: ${({variant}) => variant === DefaultComponentVariantType.TERTIARY ? theme.star.unified.filtersColors.tertiary.bgWrapperColor : theme.star.racingNextOff.slider.bgColorSecondary};
    display: flex;
    flex-flow: row wrap;
    gap: 12px;
    justify-content: space-between;
    padding: ${({variant}) => variant === DefaultComponentVariantType.TERTIARY ? '0 12px 12px' : '4px 8px 8px'};
`);

interface SportPageHeaderPropsType {
    variant: DefaultComponentVariantType | null;
}

const setSportPageHeader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            @media ${theme.star.mediaQuery.tablet} {
                border-radius: ${theme.star.radius.medium} ${theme.star.radius.medium} 0 0;
            }
        `;
    }
    return css``;
};

export const SportPageHeader = withConfig(theme => styled(HomePageHeader, { label: 'SportPageHeader' })<SportPageHeaderPropsType>`
    padding: 12px;
    ${({ variant }): SerializedStyles => setSportPageHeader(variant, theme)}
`);

interface RacesSliderPropsType {
    variant: DefaultComponentVariantType | null;
}

const setRacesSliderSection = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            @media ${theme.star.mediaQuery.tablet} {
                margin: 0 8px;
                border-radius: 0 0 ${theme.star.radius.medium} ${theme.star.radius.medium};
            }
        `;
    }
    return css``;
};

export const RacesSliderSection = withConfig(theme => styled('section', { label: 'RacesSliderSection' })<RacesSliderPropsType>`
    ${({ variant }): SerializedStyles => setRacesSliderSection(variant, theme)}
    background-color: ${theme.star.racingNextOff.slider.bgColor};
    color: ${theme.star.racingNextOff.slider.txtColor};
    overflow: hidden;
    position: relative;
`);

const setRacesSliderInfo = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background-color: ${theme.star.racingNextOff.slider.bgColorSecondary};
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            border-top: 1px solid ${theme.star.eventsTableColors.borderColor};
            padding: 15px 5px 10px 5px;
            position: relative;
            text-align: center;
            flex: 1;
            justify-content: center;
            display: flex;
            align-items: center;
            min-height: 320px;
            flex-flow: column;
            background: transparent;
            gap: 16px;
            font-size: 1.4rem;
            font-weight: 600;
            line-height: 1.4286;
        `;
    }
    return css`
        background: transparent;
        border-top: none;
        padding: 10px 5px;
        position: relative;
        text-align: center;
    `;
};

export const RacesSliderInfo = withConfig(theme => styled('div', { label: 'RacesSliderInfo' })<RacesSliderPropsType>`
    ${({ variant }): SerializedStyles => setRacesSliderInfo(variant, theme)};
    border-top: none;
    padding: 10px 5px;
    position: relative;
    text-align: center;
`);

export const SliderHeader = withConfig(theme => styled('h2', { label: 'SliderHeader' })`
    color: ${theme.star.collapse.section.txtColor};
    font-size: ${theme.star.fontSize.regular};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.5;
    margin: 0;
    user-select: none;
`);

export const RacesTypeSelectButtonsWrapper = styled('div', { label: 'RacesTypeSelectButtonsWrapper' })`
    display: flex;
    position: relative;
`;

export const SwitchWrapper = styled('div', { label: 'SwitchWrapper' })`
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    gap: 8px;
    justify-content: end;
    width: auto;
`;

export const SwitchLabel = withConfig(theme => styled('span', { label: 'SwitchLabel' })`
    color: ${theme.star.racingNextOff.toggle.txtColor};
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.xMedium};
    line-height: 1.4286;
    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.small};
    }
`);

interface NextRacesWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

const setNextRacesWrapper = (variant: DefaultComponentVariantType | null): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            &:first-of-type {
                margin-top: 8px;
            }
        `;
    }
    return css``;
};

export const NextRacesWrapper = styled('div', { label: 'NextRacesWrapper' })<NextRacesWrapperPropsType>`
    ${({ variant }): SerializedStyles => setNextRacesWrapper(variant)}
`;
